import { fetchLiveCart } from '@theme/gullyo-cart-sync';

/**
 * Keeps cart display currency aligned with selected country/market and checkout.
 */
(function gullyoCartLocalization() {
  const SYNC_FLAG = 'gullyo-localization-pending';
  const PENDING_CHECKOUT_FLAG = 'gullyo-pending-checkout';
  const CHECKOUT_BLOCK_CLASS = 'gullyo-cart-checkout-blocked';

  function getSelectedCountry() {
    return document.body?.dataset.gullyoCountry || '';
  }

  function getSelectedCurrency() {
    return (
      document.body?.dataset.gullyoCurrency ||
      window.Shopify?.currency?.active ||
      ''
    ).toUpperCase();
  }

  function getCartComponents() {
    return Array.from(document.querySelectorAll('cart-items-component'));
  }

  function setSyncing(syncing, message) {
    getCartComponents().forEach((component) => {
      component.classList.toggle('gullyo-cart--syncing', syncing);
      component.dataset.gullyoSyncing = syncing ? 'true' : 'false';
    });

    document.querySelectorAll('[data-gullyo-cart-sync-message]').forEach((node) => {
      node.hidden = !syncing;
      if (message && node.firstElementChild) {
        node.firstElementChild.textContent = message;
      }
    });

    document.body?.classList.toggle('gullyo-cart-localization-syncing', syncing);
  }

  function setCheckoutBlocked(blocked) {
    document.body?.classList.toggle(CHECKOUT_BLOCK_CLASS, blocked);
    document.querySelectorAll('#checkout, [name="checkout"]').forEach((button) => {
      if (!(button instanceof HTMLButtonElement)) return;
      if (blocked) {
        button.dataset.gullyoCheckoutDisabled = button.disabled ? 'true' : 'false';
        button.disabled = true;
      } else if (button.dataset.gullyoCheckoutDisabled !== 'true') {
        button.disabled = false;
      }
    });
  }

  function showCheckoutVerificationError() {
    setCheckoutBlocked(true);
    setSyncing(true, 'We could not verify your bag. Please reload and try again.');
  }

  function syncHeaderCartCountFromServer(cart) {
    const itemCount = cart?.item_count ?? 0;
    if (window.GullyoCartSync?.syncHeaderCartCount) {
      window.GullyoCartSync.syncHeaderCartCount(itemCount);
      return;
    }

    document.dispatchEvent(
      new CustomEvent('gullyo:cart-count-sync', {
        detail: { itemCount },
      })
    );
  }

  function updateComponentCurrency(currency) {
    if (!currency) return;
    getCartComponents().forEach((component) => {
      component.dataset.currency = currency;
    });
  }

  function currenciesMatch(cartCurrency) {
    const expected = getSelectedCurrency();
    if (!expected || !cartCurrency) return true;
    return String(cartCurrency).toUpperCase() === expected;
  }

  function cartHasBlockedLines() {
    return Boolean(
      document.querySelector('.gullyo-cart-item__market-warning, [data-gullyo-line-unavailable="true"]')
    );
  }

  function navigateToCheckout() {
    window.location.assign('/checkout');
  }

  async function reconcileCart(options = {}) {
    const { reloadOnMismatch = false, showSyncMessage = false } = options;

    if (showSyncMessage) {
      setSyncing(true, 'Updating your bag for the selected country…');
    }

    try {
      const cart = await fetchLiveCart();
      updateComponentCurrency(cart.currency);
      syncHeaderCartCountFromServer(cart);

      if (currenciesMatch(cart.currency)) {
        setCheckoutBlocked(cartHasBlockedLines());
        setSyncing(false);
        return true;
      }

      if (reloadOnMismatch) {
        const url = new URL(window.location.href);
        url.searchParams.set('gullyo_cart_sync', Date.now().toString());
        window.location.assign(url.toString());
        return false;
      }

      setCheckoutBlocked(true);
      setSyncing(false);
      return false;
    } catch (error) {
      console.error('[cart-localization] Unable to verify cart currency:', error);
      showCheckoutVerificationError();
      return false;
    }
  }

  function handleCheckoutClick(event) {
    const button = event.target.closest('#checkout, [name="checkout"]');
    if (!button || button.disabled) return;

    const form = button.form || document.getElementById('cart-form');
    if (!(form instanceof HTMLFormElement)) return;

    if (document.body?.classList.contains('gullyo-cart-localization-syncing')) {
      event.preventDefault();
      event.stopPropagation();
      return;
    }

    event.preventDefault();
    event.stopPropagation();

    if (cartHasBlockedLines()) {
      setSyncing(false);
      setCheckoutBlocked(true);
      return;
    }

    setSyncing(true, 'Confirming checkout currency…');

    fetchLiveCart()
      .then((cart) => {
        if (!cart.item_count) {
          setSyncing(false);
          setCheckoutBlocked(true);
          return;
        }

        if (!currenciesMatch(cart.currency)) {
          setSyncing(true, 'Updating your bag…');
          sessionStorage.setItem(SYNC_FLAG, '1');
          sessionStorage.setItem(PENDING_CHECKOUT_FLAG, '1');
          const url = new URL(window.location.href);
          url.searchParams.set('gullyo_cart_sync', Date.now().toString());
          window.location.assign(url.toString());
          return;
        }

        if (cartHasBlockedLines()) {
          setSyncing(false);
          setCheckoutBlocked(true);
          return;
        }

        setSyncing(false);
        setCheckoutBlocked(false);
        navigateToCheckout();
      })
      .catch(() => {
        showCheckoutVerificationError();
      });
  }

  function bindLocalizationForms() {
    document.querySelectorAll('localization-form-component form').forEach((form) => {
      if (form.dataset.gullyoLocalizationBound === 'true') return;
      form.dataset.gullyoLocalizationBound = 'true';
      form.addEventListener('submit', () => {
        sessionStorage.setItem(SYNC_FLAG, '1');
        sessionStorage.removeItem('cart-count');
        setSyncing(true, 'Updating your bag for the selected country…');
      });
    });
  }

  async function resumePendingCheckout() {
    if (!sessionStorage.getItem(PENDING_CHECKOUT_FLAG)) return;

    const synced = await reconcileCart({ reloadOnMismatch: false, showSyncMessage: false });
    if (!synced || cartHasBlockedLines()) return;

    sessionStorage.removeItem(PENDING_CHECKOUT_FLAG);
    window.location.assign('/checkout');
  }

  async function init() {
    bindLocalizationForms();

    document.addEventListener('click', handleCheckoutClick, true);

    window.addEventListener('pageshow', (event) => {
      if (event.persisted) {
        reconcileCart({ reloadOnMismatch: true, showSyncMessage: true });
        return;
      }
      reconcileCart({ reloadOnMismatch: false });
    });

    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') {
        reconcileCart({ reloadOnMismatch: false });
      }
    });

    if (sessionStorage.getItem(SYNC_FLAG)) {
      sessionStorage.removeItem(SYNC_FLAG);
      await reconcileCart({ reloadOnMismatch: true, showSyncMessage: true });
      await resumePendingCheckout();
    } else {
      await reconcileCart({ reloadOnMismatch: false });
      await resumePendingCheckout();
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
})();
